<template>
    <div class="email-settings-step">
        <form ref="form" @submit.prevent>
            <h4 class="my-3"><b>Email Settings</b></h4>

            <!-- Recipients Section -->
            <div class="form-group mb-4">
                <label>Recipients <span class="text-danger">*</span></label>
                <!-- Search Bar with Floating Dropdown -->
                <div class="recipient-search-wrapper recipent-field " style="position: relative;">
                    <v-menu v-model="menuOpen" offset-y :close-on-content-click="false" max-height="400"
                        nudge-bottom="5" content-class="recipient-dropdown-content">
                        <template v-slot:activator="{ on, attrs }">
                            <div ref="searchWrapper" class="search-container position-relative" v-bind="attrs"
                                v-on="on">
                                <CInput class="search-bar" v-model="memberQuery" @input="handleInput"
                                    @focus="menuOpen = true" placeholder="Search Team Member by name" type="text">
                                </CInput>
                                <v-icon small class="bg-white position-absolute p-2"
                                    style="right: 0.8rem; top: 4px;max-width:100%!important"
                                    color="rgba(79, 79, 79, 1)">mdi-magnify</v-icon>
                            </div>
                        </template>

                        <!-- Dropdown List -->
                        <div class="bg-white shadow-sm border rounded">
                            <div class="mx-3 my-2" ref="scrollableMembers" style="max-height: 300px; overflow: auto">
                                <!-- Header inside dropdown (Optional but useful context) -->
                                <div v-if="organizationName" class="px-2 py-2 d-none">
                                    <b>{{ organizationName }} Members</b>
                                    <hr class="my-2" />
                                </div>

                                <div v-for="item in filteredMembers" :key="item.id"
                                    style="border-bottom: 1px solid #E0E0E0;border-radius: 0px!important;"
                                    class="text-dark d-flex justify-content-between align-items-center my-1 px-3 py-2 rounded">
                                    <div class="d-flex flex-column" style="overflow: hidden;">
                                        <p class="m-0 text-truncate" style="font-size: 14px; color: #3F3F3F">{{
                                            item.name }}
                                        </p>
                                        <p class="m-0 font-weight-bold text-capitalize"
                                            style="font-size: 12px; color: #3F3F3F">
                                            {{
                                                toTitleCase(item.role) }}</p>
                                    </div>
                                    <div v-if="isSelected(item.id)" class="ml-2 btn btn-sm btn-danger py-1 px-3"
                                        style="font-size: 12px;" role="button" @click="removeRecipientById(item.id)">
                                        Remove
                                    </div>
                                    <div v-else class="ml-2 btn btn-sm btn-primary py-1 px-3" style="font-size: 12px;"
                                        role="button" @click="addRecipient(item)">Add</div>
                                </div>
                                <p class="text-center text-secondary my-2"><i>Don’t see the team member in the list? Type their name to search.</i></p>

                                <!-- States -->
                                <div class="my-4 text-center text-muted" v-if="members.length === 0 && !loadingMembers">
                                    <small>Member(s) not found</small>
                                </div>
                                <div class="my-4 text-center" v-if="loadingMembers">
                                    <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                                </div>
                            </div>
                        </div>
                    </v-menu>
                </div>

                <!-- Selected Recipients Chips -->
                <div class="counter mt-3" v-if="formData.recipients && formData.recipients.length > 0">
                    {{ formData.recipients.length }} member(s) selected. <span class="text-danger" role="button"
                        @click="removeAllRecipients"><b>Remove All</b></span>
                </div>
                <div v-if="formData.recipients && formData.recipients.length > 0" class="d-flex flex-wrap mt-3">
                    <v-tooltip
                        v-for="(recipient, index) in formData.recipients"
                        :key="index"
                        top
                        :attach="true"
                        :disabled="!isRecipientInactive(recipient)"
                        content-class="recipient-status-tooltip"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <div
                                v-bind="attrs"
                                v-on="on"
                                class="d-flex contributor-box mr-2 mb-2"
                                :class="recipientBoxClass(recipient)"
                            >
                                <div class="mr-3 align-self-center">
                                    <div style="color: #3F3F3F; font-weight: 700; font-size: 14px;">{{ recipient.name }}</div>
                                    <div class="my-1"
                                        style="color: #3F3F3F; font-size: 12px; font-weight: 400;">
                                        {{ normalizeEmail(recipient?.email) }}
                                    </div>
                                </div>
                                <div class="ml-auto close-btn" role="button" @click="removeRecipient(index)">
                                    <v-icon small color="#757575">mdi-close</v-icon>
                                </div>
                            </div>
                        </template>
                        <span>This team member is inactive. This report will no longer be sent to them.</span>
                    </v-tooltip>
                </div>
                <div v-else class="text-muted font-italic mt-3 mb-4">
                    No Recipient added
                </div>
                <small v-if="validationErrors.recipients" class="text-danger d-block mt-1">{{
                    validationErrors.recipients }}</small>
            </div>

            <!-- Header Logo Selection -->
            <div class="form-group mb-4">
                <label>Header Logo</label>
                <select v-model="formData.header_logo" disabled class="form-control" style="width: 800px;">
                    <option v-for="opt in logoOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <!-- Logo Preview -->
                <div v-if="currentLogoUrl" class="mt-2 text-center border rounded p-3" style="background: #f8f9fa;">
                    <img :src="currentLogoUrl" alt="Header Logo" style="max-width: 280px; max-height: 60px;">
                </div>
            </div>

            <!-- Subject -->
            <div class="form-group mb-4">
                <label class="">Subject <span class="text-danger">*</span></label>
                <CInput v-model="formData.subject" placeholder="Input email subject" required />
            </div>

            <!-- Body (Rich Text Editor) -->
            <div class="form-group mb-4">
                <label>Body <span class="text-danger">*</span></label>
                <small v-if="validationErrors.body" class="text-danger d-block mb-1">{{ validationErrors.body }}</small>
                <div class="email-body-editor">
                    <VueEditor ref="editor" v-model="formData.body" :editorToolbar="customToolbar"
                        :editorOptions="editorOptions" @ready="onEditorReady" placeholder="Dear Recipient,

Your scheduled report has been successfully generated and is now part of your reporting subscription..." />
                    <div v-if="isUploading" class="upload-overlay">
                        <div class="upload-spinner"></div>
                        <span>Uploading...</span>
                    </div>
                </div>
            </div>
            <div class="mb-4 pb-2" style="width: 100%;border-bottom: 1px solid #ccc;cursor: default!important;">
                <label>Attachment</label>
                <div class="mb-2" style="background-color: #EDF4FE; padding: 10px; border-radius: 5px; width: 100%  ;">
                    <span class="mdi mdi-file-document-outline"></span>
                    <span class="ml-2">{{ reportFilename }}</span>
                </div>

                <span class="text-muted">The filename will include the generation date automatically</span>
            </div>
        </form>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment-timezone';
import { normalizeEmail as normalizeEmailValue } from '../../../shared/schedule-report-normalizers';
import CInput from '../../c-input.vue';
import { VueEditor } from 'vue2-editor';

const FIXED_TIME_ZONE = 'Etc/GMT+5';

export default {
    name: 'EmailSetting',
    components: {
        CInput,
        VueEditor
    },
    props: {
        value: {
            type: Object,
            default: () => ({
                recipients: [],
                subject: '',
                body: '',
                header_logo: 'fastvisa'
            })
        },
        reportData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            validationErrors: {
                recipients: '',
                body: ''
            },
            formData: {
                recipients: [],
                subject: '',
                body: '<p>Dear Recipient,</p><p><br></p><p>Your scheduled report has been successfully generated and delivered as part of your reporting subscription.</p><p>The report file is included as an attachment in this email for your convenience. It contains the most recent data based on the predefined configuration and schedule.</p><p><br></p><p>If you have any questions regarding the contents of this report or would like to make changes to your reporting preferences, please do not hesitate to contact our support team.</p>',
                header_logo: 'fastvisa'
            },
            memberQuery: '',
            loadingMembers: false,
            page: 1,
            menuOpen: false,
            isInternalUpdate: false,
            // VueEditor
            quillInstance: null,
            isUploading: false,
            uploadedBlobs: [],
            previousImages: [],
            directUploadsUrl: '/rails/active_storage/direct_uploads',
            customToolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['link', 'image'],
                [{ 'color': [] }, { 'background': [] }],
                ['clean']
            ],
            editorOptions: {
                modules: {}
            },
            logoOptions: [
                { value: 'fastvisa', label: 'FastVisa (Default)' },
                { value: 'charter', label: 'Charter Immigration' },
                // { value: 'none', label: 'No Logo' }
            ]
        };
    },
    computed: {
        ...mapGetters({
            members: 'reportSchedules/members',
            organizationId: 'reportTemplates/organizationId',
            organization: 'reportTemplates/organization',
            organizationCode: 'organizationCode',
            logoPaths: 'logoPaths',
        }),
        organizationName() {
            return this.$store.state.reportList.organization?.name || '';
        },
        filteredMembers() {
            if (!this.members || !Array.isArray(this.members)) return [];
            return this.members.filter(m => m && m.id);
        },
        assetHost() {
            return window.location.origin;
        },
        currentLogoUrl() {
            if (this.formData.header_logo === 'none') return null;
            const resolvedFastvisa = this.resolveLogoUrl(this.logoPaths?.fastvisa) || `${this.assetHost}/assets/logos/logo-fastvisa.png`;
            const resolvedCharter = this.resolveLogoUrl(this.logoPaths?.charter) || `${this.assetHost}/assets/logos/logo-charter.png`;

            if (this.formData.header_logo === 'fastvisa') return resolvedFastvisa;
            if (this.formData.header_logo === 'charter') return resolvedCharter;
            return resolvedFastvisa;
        },
        authorizationToken() {
            // Access token from store (same pattern as report-list.js)
            return this.$store.state.reportList?.authorization?.token ||
                this.$store.state.authorization?.token || '';
        },
        reportFilename() {
            const name = this.reportData?.name || 'Report';
            const frequency = (this.reportData?.schedule_type || '').toLowerCase();
            const frequencyLabel = frequency === 'monthly'
                ? 'Monthly'
                : frequency === 'weekly'
                    ? 'Weekly'
                    : '';
            const scheduleDay = this.reportData?.schedule_day || 'monday';
            const scheduleTime = this.reportData?.schedule_time || '09:00';
            // FE note: schedule_day/time are client-only inputs (backend only stores next_run_at/last_run_at).
            // We compute the preview filename date based on those fields for UI consistency.
            const timeParts = scheduleTime.split(':');
            const hours = parseInt(timeParts[0], 10) || 9;
            const minutes = parseInt(timeParts[1], 10) || 0;
            const now = moment().tz(FIXED_TIME_ZONE);
            let nextDate = moment().tz(FIXED_TIME_ZONE);
            nextDate = nextDate.hours(hours).minutes(minutes).seconds(0).milliseconds(0);

            if (frequency === 'weekly') {
                const targetDay = now.day(scheduleDay).isoWeekday();
                nextDate = moment().tz(FIXED_TIME_ZONE)
                    .isoWeekday(targetDay)
                    .hours(hours)
                    .minutes(minutes)
                    .seconds(0)
                    .milliseconds(0);
                // Always schedule the next occurrence, not the same day.
                if (nextDate.isSame(now, 'day') || nextDate.isBefore(now)) {
                    nextDate.add(1, 'week');
                }
            } else if (frequency === 'monthly') {
                nextDate = moment().tz(FIXED_TIME_ZONE)
                    .date(1)
                    .hours(hours)
                    .minutes(minutes)
                    .seconds(0)
                    .milliseconds(0);
                if (nextDate.isBefore(now)) {
                    nextDate.add(1, 'month');
                }
            }

            const dateLabel = nextDate.format('MM-DD-YYYY');
            const suffix = frequencyLabel
                ? ` - ${frequencyLabel} Report ${dateLabel}`
                : ` - ${dateLabel}`;
            return `${name}${suffix}.csv`;
        }
    },
    watch: {
        value: {
            handler(val) {
                if (this.isInternalUpdate) return;
                // Preserve defaults if parent passes empty values
                const defaultBody = '<p>Dear Recipient,</p><p><br></p><p>Your scheduled report has been successfully generated and delivered as part of your reporting subscription.</p><p>The report file is included as an attachment in this email for your convenience. It contains the most recent data based on the predefined configuration and schedule.</p><p><br></p><p>If you have any questions regarding the contents of this report or would like to make changes to your reporting preferences, please do not hesitate to contact our support team.</p>';
                this.formData = {
                    ...val,
                    recipients: this.normalizeRecipients(val?.recipients),
                    body: val?.body || defaultBody,
                    header_logo: val?.header_logo || 'fastvisa'
                };
            },
            deep: true,
            immediate: true
        },
        formData: {
            handler(val) {
                this.isInternalUpdate = true;
                this.$emit('input', {
                    ...val,
                    recipients: this.normalizeRecipients(val?.recipients),
                });
                this.$nextTick(() => {
                    this.isInternalUpdate = false;
                });
            },
            deep: true
        },
        'formData.body'(newVal, oldVal) {
            // Clear validation error if valid
            const bodyText = newVal?.replace(/<[^>]*>/g, '').trim();
            if (bodyText) {
                this.validationErrors.body = '';
            }

            if (this.quillInstance && this.uploadedBlobs.length > 0) {
                this.$nextTick(() => {
                    this.checkDeletedImages();
                });
            }
        },
        'formData.recipients'(newVal) {
            if (newVal && newVal.length > 0) {
                this.validationErrors.recipients = '';
            }
        }
    },
    methods: {
        normalizeEmail(email) {
            return normalizeEmailValue(email) || '';
        },
        normalizeRecipients(recipients) {
            if (!Array.isArray(recipients)) return [];
            return recipients.map((recipient) => {
                if (!recipient || typeof recipient !== 'object') return recipient;
                return {
                    ...recipient,
                    email: this.normalizeEmail(recipient.email),
                };
            });
        },
        isRecipientInactive(recipient) {
            if (!recipient) return false;
            const status = (recipient?.status || '').toString().toLowerCase();
            return status === 'inactive';
        },
        recipientBoxClass(recipient) {
            return this.isRecipientInactive(recipient) ? 'box-disabled' : 'box-enabled';
        },
        resolveLogoUrl(path) {
            if (!path) return null;
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${this.assetHost}${path}`;
        },
        // Validation method - exposed for parent to call via ref
        validate() {
            this.validationErrors = { recipients: '', body: '' };
            let isValid = true;

            // 1. Native Validation (Subject)
            if (this.$refs.form && this.$refs.form.reportValidity && !this.$refs.form.reportValidity()) {
                isValid = false;
            }

            // 2. Custom Validation (Recipients)
            if (!this.formData.recipients || this.formData.recipients.length === 0) {
                this.validationErrors.recipients = 'At least one recipient is required';
                isValid = false;
            }

            // 3. Custom Validation (Body)
            // check if body is empty or just has html tags
            const bodyText = this.formData.body?.replace(/<[^>]*>/g, '').trim();
            if (!bodyText) {
                this.validationErrors.body = 'Email body is required';
                isValid = false;
            }

            return isValid;
        },

        // Member search methods
        searchMember() {
            this.page = 1;
            this.loadingMembers = true;
            this.$store.dispatch('reportSchedules/getMembers', { query: this.memberQuery })
                .finally(() => {
                    this.loadingMembers = false;
                });
        },
        searchWithDebounce: _.debounce(function () {
            this.searchMember();
        }, 500),

        handleInput(val) {
            this.menuOpen = true;
            this.searchWithDebounce();
        },

        loadMore() {
            // Pagination logic if supported
        },
        handleScroll(e) {
            const container = e.target;
            if (
                container.scrollTop + container.clientHeight >=
                container.scrollHeight - 100 && !this.loadingMembers
            ) {
                this.loadMore();
            }
        },

        addRecipient(member) {
            if (member && member.id && this.formData.recipients) {
                const exists = this.formData.recipients.some(r => r && r.id === member.id);
                if (!exists) {
                    const nextRecipient = {
                        id: member.id,
                        name: member.name,
                        email: this.normalizeEmail(member.email),
                        role: member.role,
                        status: member.status,
                    };
                    this.formData.recipients = [...this.formData.recipients, nextRecipient];
                }
            }
        },
        removeRecipient(index) {
            if (!this.formData.recipients) return;
            this.formData.recipients = this.formData.recipients.filter((_, recipientIndex) => recipientIndex !== index);
        },
        removeRecipientById(id) {
            if (!this.formData.recipients) return;
            const index = this.formData.recipients.findIndex(r => r && r.id === id);
            if (index !== -1) {
                this.removeRecipient(index);
            }
        },
        removeAllRecipients() {
            this.formData.recipients = [];
        },
        isSelected(id) {
            if (!this.formData.recipients) return false;
            return this.formData.recipients.some(r => r && r.id === id);
        },
        toTitleCase(str) {
            if (!str) return '';
            return str.toLowerCase().split('_').map(function (word) {
                return (word.charAt(0).toUpperCase() + word.slice(1));
            }).join(' ');
        },

        // VueEditor methods
        onEditorReady(quill) {
            this.quillInstance = quill;

            // Custom image handler
            const toolbar = quill.getModule('toolbar');
            toolbar.addHandler('image', () => {
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.click();

                input.onchange = () => {
                    const file = input.files[0];
                    if (file) {
                        this.uploadImageToS3(file, quill);
                    }
                };
            });

            // Handle paste event for images
            quill.root.addEventListener('paste', (e) => {
                const clipboardData = e.clipboardData || window.clipboardData;
                const items = clipboardData.items;

                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                        e.preventDefault();
                        const file = items[i].getAsFile();
                        this.uploadImageToS3(file, quill);
                        break;
                    }
                }
            });

            // Handle drag and drop for images
            quill.root.addEventListener('drop', (e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;

                for (let i = 0; i < files.length; i++) {
                    if (files[i].type.indexOf('image') !== -1) {
                        this.uploadImageToS3(files[i], quill);
                    }
                }
            });

            // Prevent default drag behavior
            quill.root.addEventListener('dragover', (e) => {
                e.preventDefault();
            });
        },

        uploadImageToS3(file, quill) {
            const maxSize = 1 * 1024 * 1024; // 1MB
            if (file.size > maxSize) {
                alert('Image too large! Maximum size is 1MB.');
                return;
            }

            this.isUploading = true;

            const upload = new ActiveStorage.DirectUpload(file, this.directUploadsUrl);

            upload.create((error, blob) => {
                this.isUploading = false;

                if (error) {
                    console.error('Upload error:', error);
                    alert('Failed to upload image: ' + error);
                } else {
                    const imageUrl = `${window.location.origin}/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`;

                    this.uploadedBlobs.push({
                        signedId: blob.signed_id,
                        url: imageUrl
                    });

                    const range = quill.getSelection() || { index: quill.getLength() };
                    quill.insertEmbed(range.index, 'image', imageUrl);
                    quill.setSelection(range.index + 1);

                    this.updateTrackedImages();
                }
            });
        },

        updateTrackedImages() {
            const images = this.quillInstance.root.querySelectorAll('img');
            this.previousImages = Array.from(images).map(img => img.src);
        },

        checkDeletedImages() {
            const currentImages = Array.from(
                this.quillInstance.root.querySelectorAll('img')
            ).map(img => img.src);

            const deletedImages = this.previousImages.filter(
                url => !currentImages.includes(url)
            );

            deletedImages.forEach(url => {
                const blob = this.uploadedBlobs.find(b => b.url === url);
                if (blob) {
                    this.deleteBlobFromS3(blob.signedId);
                    this.uploadedBlobs = this.uploadedBlobs.filter(b => b.url !== url);
                }
            });

            this.previousImages = currentImages;
        },

        async deleteBlobFromS3(signedId) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
                await axios.delete('/api/v1/documents/purge_unattached_document', {
                    data: { signed_id: signedId },
                    headers: {
                        'X-CSRF-Token': csrfToken,
                        'Authorization': this.authorizationToken
                    }
                });
            } catch (error) {
                console.error('Failed to delete image from S3:', error);
            }
        }
    },
    mounted() {
        this.searchMember();
        // Auto-select charter logo if organization code contains 'charter'
        if (this.organizationCode && this.organizationCode.toLowerCase().includes('charter')) {
            this.formData.header_logo = 'charter';
        }
    },
    beforeDestroy() {
        // Cleanup
    }
}
</script>

<style scoped lang="scss">
.email-settings-step {
    padding-bottom: calc(140px + env(safe-area-inset-bottom));
}

.email-body-editor {
    position: relative;
}

.email-body-editor ::v-deep .ql-container.ql-snow {
    height: auto;
}

.email-body-editor ::v-deep .ql-editor {
    min-height: 180px;
    max-height: 320px;
    overflow-y: auto;
}

.recipent-field {
    max-width: 800px;
}

.box-enabled {
    background: #EDF4FE;
    border-radius: 4px;
    padding: 10px;
    border: 1px solid #EDF4FE;
    min-width: 200px;
}

.box-disabled {
    background: #F4F4F4;
    border-radius: 4px;
    padding: 10px;
    border: 1px solid #E0E0E0;
    min-width: 200px;
}

.contributor-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.close-btn {
    cursor: pointer;
    opacity: 0.6;

    &:hover {
        opacity: 1;
    }
}

.cursor-pointer {
    cursor: pointer;
}

.upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100;
    gap: 10px;
    font-weight: 500;
    color: #1976d2;
}

.upload-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top: 4px solid #1976d2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .email-settings-step {
        padding-bottom: calc(160px + env(safe-area-inset-bottom));
    }
}

</style>

<style lang="scss">
.recipient-status-tooltip {
    max-width: 260px;
    font-size: 12px;
    color: #3F3F3F;
    line-height: 1.4;
    background: #ffffff;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    padding: 8px 12px;
}
</style>
